/** The week's days: hours captured, how much is released, and earlier days left unreleased. */
import type { FC } from "react";

import { cn } from "@/lib/utils";

import type { DayStat } from "../model";
import { formatHours } from "../utils";

type Props = {
  dates: string[];
  today: string;
  selected: string;
  stats: Record<string, DayStat>;
  onSelect: (date: string) => void;
};

const parts = (iso: string) => {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return {
    weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
    day: d,
    weekend: date.getDay() === 0 || date.getDay() === 6
  };
};

export const WeekStrip: FC<Props> = ({
  dates,
  today,
  selected,
  stats,
  onSelect
}) => (
  <div className="shrink-0 border-b border-line-2 px-6 py-2.5">
    <div
      className="grid grid-cols-7 divide-x divide-line-1 overflow-hidden rounded-lg ring-1 ring-line-2"
      data-testid="week-strip"
    >
      {dates.map((iso) => {
        const { weekday, day, weekend } = parts(iso);
        const st = stats[iso] ?? {
          date: iso,
          captured: 0,
          released: 0,
          unreleased: 0
        };
        const isSel = iso === selected;
        const future = iso > today;
        const late = st.unreleased > 0 && iso < today;
        const empty = future || (weekend && !st.captured);
        return (
          <button
            key={iso}
            type="button"
            disabled={future}
            data-date={iso}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(iso);
            }}
            title={late ? `${st.unreleased} unreleased` : undefined}
            className={cn(
              "relative flex h-8 min-w-0 items-center gap-1.5 px-3 text-left text-base transition-colors",
              isSel ? "bg-accent-soft" : "hover:bg-fill-1",
              future && "cursor-default hover:bg-transparent"
            )}
          >
            <span
              className={cn(
                "truncate",
                isSel ? "font-medium text-fg" : "text-fg-2",
                future && "text-fg-4"
              )}
            >
              {weekday} {day}
            </span>
            {iso === today ? (
              <span className="text-sm text-fg-3">Today</span>
            ) : null}
            <span className="flex-1" />
            {late ? (
              <span className="size-1.5 shrink-0 rounded-full bg-warn" />
            ) : null}
            <span
              className={cn(
                "tabular-nums",
                late
                  ? "font-medium text-warn"
                  : empty || !st.captured
                  ? "text-fg-4"
                  : "font-medium text-fg"
              )}
            >
              {empty ? "–" : formatHours(st.captured)}
            </span>
            {isSel ? (
              <span className="absolute inset-x-0 bottom-0 h-0.5 bg-accent-seed" />
            ) : null}
          </button>
        );
      })}
    </div>
  </div>
);
